import React, { useCallback, useMemo, useRef, useLayoutEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Flex, TextProps } from 'rebass'
import { useNewTranslation } from 'hooks/index'
import _ from 'lodash'
import Text from 'particles/Text'

import {
    makeCustomSubmissionContentSelector,
    selectReviewPendingSubmission,
    makeSubmissionContentSelector,
    setMediaData,
    setSelectedMediaIndex,
    SubmissionProgressProps,
    selectSelectedSubmissionView,
    setMediaModalOpen,
    openCaptionDetailModal,
    selectSelectedCampaignSummary,
} from 'state/review'
import { State } from 'state'
import { theme } from 'theme'
import MediaViewer, { MediaData } from 'components/MediaViewer'
import { StepState, TaskState } from 'state/tasks'
import styled from 'particles/themes/styled'
import { SUBMISSION_VIEW_ENUM } from 'state/submissions'
import { useTranslation } from 'react-i18next'
import { STEP_TYPE_ENUM } from 'services/api/constants'

const XIAOHONGSHU_STEP_TYPES: STEP_TYPE_ENUM[] = [
    STEP_TYPE_ENUM.SUBMIT_CONTENT_XIAOHONGSHU_POST,
    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_XIAOHONGSHU_POST_URL,
    STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_XIAOHONGSHU_POST,
]

const MediaBtnWrapper: React.FC<any> = ({
    submissionContent,
    mt = theme.spacing.xxs,
    photoPadding = theme.spacing.xxs,
}) => {
    const dispatch = useDispatch()

    const setMedia = useCallback((data: MediaData[]) => dispatch(setMediaData(data)), [])
    const setMediaIndex = useCallback((index: number) => dispatch(setSelectedMediaIndex(index)), [])
    const setMediaModal = useCallback((isOpen: boolean) => dispatch(setMediaModalOpen(isOpen)), [])
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const needToOpenPopup = selectedSubmissionView !== SUBMISSION_VIEW_ENUM.PENDING

    return (
        <Flex>
            <Box p={photoPadding} mx="auto" mt={mt}>
                <MediaViewer
                    mediaData={submissionContent as MediaData}
                    showMediaIndex={0}
                    showPopup={false}
                    setModalOpen={needToOpenPopup ? setMediaModal : undefined}
                    setMediaData={setMedia}
                    setSelectedMediaIndex={setMediaIndex}
                    maxHeight="260px"
                    playVideo={selectedSubmissionView !== SUBMISSION_VIEW_ENUM.PENDING}
                    showCarouselFooterEmptyBlock
                />
            </Box>
        </Flex>
    )
}

interface CaptionTextProps extends TextProps {
    isVerifyMode: boolean
}

const CaptionText = styled(Text)<CaptionTextProps>`
    display: -webkit-box;
    word-break: break-word;
    ${(props: CaptionTextProps) =>
        props.isVerifyMode
            ? {
                  fontSize: '0.8em',
              }
            : {
                  WebkitLineClamp: 11,
              }}
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`

const CaptionWithReadMore = styled.div`
    width: 100%;
    margin: ${theme.spacing.xxs} 0;
    text-align: justify;
    position: relative;
    font-size: ${theme.newFontSizes.xs};
    line-height: 1.5;
    color: ${theme.newColors.neutral700};
    word-break: break-word;

    .caption-text {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .read-more-wrapper {
        position: absolute;
        bottom: 0;
        right: 0;
        display: flex;
        align-items: center;
        background: linear-gradient(to right, rgba(255, 255, 255, 0), white 15%, white);
        padding-left: 1rem;
    }

    .read-more-ellipsis {
        color: ${theme.colors.mineShaft};
        margin-right: 4px;
    }

    .read-more-btn {
        color: ${({ theme }): string => theme.colors.text.action};
        cursor: pointer;
        font-size: ${theme.newFontSizes.xs};
        font-weight: 400;

        &:hover {
            text-decoration: underline;
        }
    }
`

interface SubmissionContentProps {
    submissionProgress: SubmissionProgressProps
    step: StepState
    task?: TaskState
    showCaption?: boolean
    mt?: string
    photoPadding?: string
    showCustomStepTitle?: boolean
}

const CustomSubmissionContent: React.FC<any> = ({
    blockData,
    submissionProgress,
    mt = theme.spacing.xxs,
    photoPadding = theme.spacing.xxs,
    showCustomStepTitle = true,
}) => {
    const selectSubmissionContents = useMemo(makeCustomSubmissionContentSelector, [])
    const submissionData = useSelector((state: State) =>
        selectSubmissionContents(state, {
            submissionProgress,
            block: blockData,
        }),
    )
    const isVerifyMode = useSelector(selectReviewPendingSubmission)

    if (!blockData) {
        return null
    }

    let SubmissionComponent
    if (blockData.type === 'photo_submission') {
        SubmissionComponent = submissionData ? (
            <MediaBtnWrapper
                submissionContent={submissionData}
                submissionProgress={submissionProgress}
                isCustomStep={true}
                mt={mt}
                photoPadding={photoPadding}
            />
        ) : null
    } else if (blockData.type === 'text_submission') {
        SubmissionComponent = (
            <CaptionText isVerifyMode={!_.isEmpty(isVerifyMode)} my={theme.spacing.xxs}>
                {submissionData}
            </CaptionText>
        )
    } else if (blockData.type === 'url_submission') {
        SubmissionComponent = (
            <Box my={theme.spacing.xxs}>
                <a
                    onClick={e => e.stopPropagation()}
                    style={{ wordBreak: 'break-all' }}
                    href={submissionData}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {submissionData}
                </a>
            </Box>
        )
    } else {
        return null
    }
    return (
        <>
            {showCustomStepTitle && <Text scale="bodyS700">{blockData.title}</Text>}
            {SubmissionComponent}
        </>
    )
}

const PredefinedSubmissionContent: React.FC<SubmissionContentProps> = ({
    submissionProgress,
    step,
    showCaption = true,
    mt = theme.spacing.xxs,
    photoPadding = theme.spacing.xxs,
}) => {
    const [newT] = useNewTranslation()
    const [t] = useTranslation()
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const selectSubmissionContents = useMemo(makeSubmissionContentSelector, [])
    const { submissionContent, captionData, url, engagementData, video_code, post_title } = useSelector(
        (state: State) =>
            selectSubmissionContents(state, {
                submissionProgress,
                step,
            }),
    )

    // For PENDING view, caption and post_title are not extracted by selector, so get them directly from submissionProgress.data
    const isPendingView = selectedSubmissionView === SUBMISSION_VIEW_ENUM.PENDING
    const pendingCaption = isPendingView ? submissionProgress.data?.caption : null
    const displayCaption = pendingCaption || captionData
    const pendingPostTitle = isPendingView ? submissionProgress.data?.post_title : null
    const displayPostTitle = pendingPostTitle || post_title
    const isXiaohongshuStep = XIAOHONGSHU_STEP_TYPES.includes(step.type)

    const dispatch = useDispatch()
    const captionRef = useRef<HTMLDivElement>(null)
    const captionDataRef = useRef<HTMLDivElement>(null)

    // Trigger recalculation after DOM is updated
    const [updateTrigger, setUpdateTrigger] = useState(0)
    useLayoutEffect(() => {
        setUpdateTrigger(prev => prev + 1)
    }, [displayCaption, captionData])

    // Check if captions exceed 3 lines - recalculate after DOM updates
    const showReadMore = useMemo(() => {
        if (!captionRef.current || !displayCaption) return false
        const lineHeight = parseFloat(getComputedStyle(captionRef.current).lineHeight)
        const height = captionRef.current.scrollHeight
        const lines = Math.round(height / lineHeight)
        return lines > 3
    }, [displayCaption, updateTrigger])

    const showReadMoreForCaptionData = useMemo(() => {
        if (!captionDataRef.current || !captionData) return false
        const lineHeight = parseFloat(getComputedStyle(captionDataRef.current).lineHeight)
        const height = captionDataRef.current.scrollHeight
        const lines = Math.round(height / lineHeight)
        return lines > 3
    }, [captionData, updateTrigger])

    const handleOpenCaptionModal = useCallback(
        (caption: string) => (e: React.MouseEvent) => {
            e.stopPropagation()
            dispatch(
                openCaptionDetailModal({
                    isOpen: true,
                    caption: caption || '',
                    mediaData: submissionContent,
                    comment: submissionProgress.comment,
                    timestamp: submissionProgress.submitted_at,
                    country: selectedCampaignSummary?.country,
                }),
            )
        },
        [
            dispatch,
            submissionContent,
            submissionProgress.comment,
            submissionProgress.submitted_at,
            selectedCampaignSummary?.country,
        ],
    )

    const shouldShowCaptionUnderMedia = isPendingView && displayCaption

    return (
        <Box>
            {url && (
                <>
                    <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                        {newT('submission.table.urlLabel')}
                    </CaptionText>
                    <Box sx={{ textAlign: 'left' }} my={theme.spacing.xxs}>
                        <a
                            style={{
                                wordBreak: 'break-all',
                            }}
                            href={url}
                            onClick={e => e.stopPropagation()}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {url}
                        </a>
                    </Box>
                </>
            )}
            {video_code && (
                <>
                    <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                        {newT('submission.table.videoCodeLabel')}
                    </CaptionText>
                    <Box sx={{ textAlign: 'left' }} my={theme.spacing.xxs}>
                        <a
                            style={{
                                wordBreak: 'break-all',
                            }}
                            href={video_code}
                        >
                            {video_code}
                        </a>
                    </Box>
                </>
            )}
            {submissionContent && (
                <>
                    <MediaBtnWrapper
                        submissionContent={submissionContent}
                        submissionProgress={submissionProgress}
                        mt={mt}
                        photoPadding={photoPadding}
                    />

                    {isXiaohongshuStep && displayPostTitle && <Text scale="bodyM700">{displayPostTitle}</Text>}

                    {shouldShowCaptionUnderMedia && (
                        <CaptionWithReadMore onClick={e => e.stopPropagation()}>
                            <div ref={captionRef} className="caption-text">
                                {displayCaption}
                            </div>
                            {showReadMore && (
                                <div className="read-more-wrapper" onClick={e => e.stopPropagation()}>
                                    <Text className="read-more-ellipsis">...</Text>
                                    <Text
                                        className="read-more-btn"
                                        color="skyBlue"
                                        onClick={handleOpenCaptionModal(displayCaption || '')}
                                    >
                                        {t('button.readMore')}
                                    </Text>
                                </div>
                            )}
                        </CaptionWithReadMore>
                    )}
                </>
            )}
            {captionData && showCaption && !isPendingView && (
                <CaptionWithReadMore onClick={e => e.stopPropagation()}>
                    <div ref={captionDataRef} className="caption-text">
                        {captionData}
                    </div>
                    {showReadMoreForCaptionData && (
                        <div className="read-more-wrapper" onClick={e => e.stopPropagation()}>
                            <Text className="read-more-ellipsis">...</Text>
                            <Text
                                className="read-more-btn"
                                color="skyBlue"
                                onClick={handleOpenCaptionModal(captionData || '')}
                            >
                                {t('button.readMore')}
                            </Text>
                        </div>
                    )}
                </CaptionWithReadMore>
            )}
            {post_title && !isXiaohongshuStep && (
                <Text scale="bodyS700" textAlign="left">
                    {post_title}
                </Text>
            )}
            {engagementData && (
                <>
                    {engagementData.view_count_screenshot_files && (
                        <MediaBtnWrapper
                            submissionContent={engagementData.view_count_screenshot_files}
                            submissionProgress={submissionProgress}
                            mt={mt}
                            photoPadding={photoPadding}
                        />
                    )}
                    {engagementData.insights_screenshot_files && (
                        <MediaBtnWrapper
                            submissionContent={engagementData.insights_screenshot_files}
                            submissionProgress={submissionProgress}
                            mt={mt}
                            photoPadding={photoPadding}
                        />
                    )}
                    {engagementData.follower_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('submission.table.followerCountLabel')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.follower_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_like_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.like')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_like_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_reply_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.reply')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_reply_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_retweet_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.retweet')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_retweet_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_engagement_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.engagement')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_engagement_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_comment_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.numberComments')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_comment_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_view_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.view')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_view_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_share_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.share')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_share_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_tag_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.tag')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_tag_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_sticker_tap_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.stickerTap')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_sticker_tap_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_reach_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.reach')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_reach_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_impression_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.impression')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_impression_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_cta_click_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT(
                                    'engagementMetric.ctaButtonClicksBoostedPosts',
                                    'Number of CTA Button Clicks (for boosted posts)',
                                )}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_cta_click_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_save_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.save')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_save_count}
                            </CaptionText>
                        </>
                    )}
                </>
            )}
        </Box>
    )
}

const SubmissionContent: React.FC<SubmissionContentProps> = ({
    submissionProgress,
    step,
    showCaption = true,
    mt = theme.spacing.xxs,
    photoPadding = theme.spacing.xxs,
    showCustomStepTitle = true,
}) => {
    return step.type === 'custom' ? (
        <Box mt={mt} sx={{ textAlign: 'left' }}>
            {step.data.blocks.map((blockData: any, index: number) => {
                return (
                    <CustomSubmissionContent
                        key={index}
                        blockData={blockData}
                        submissionProgress={submissionProgress}
                        mt={mt}
                        photoPadding={photoPadding}
                        showCustomStepTitle={showCustomStepTitle}
                    />
                )
            })}
        </Box>
    ) : (
        <PredefinedSubmissionContent
            submissionProgress={submissionProgress}
            step={step}
            showCaption={showCaption}
            mt={mt}
            photoPadding={photoPadding}
        />
    )
}

export default React.memo(SubmissionContent)
